"use client";

import ScreenLayout from "@/components/layout/screen-layout";
import React from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

interface ChartData {
  name: string;
  value: number;
  color: string;
}

interface ChartSection {
  id: string;
  question: string;
  data: ChartData[];
}

const chartSections: ChartSection[] = [
  {
    id: "01",
    question: "What do you seek on a health-insurance site?",
    data: [
      { name: "Clear plan comparison", value: 42, color: "#2877FF" },
      { name: "Easy premium breakdown", value: 28, color: "#70CAE3" },
      { name: "Benefits explanations", value: 21, color: "#579AFF" },
      { name: "Fast purchase flow", value: 9, color: "#AED8FF" },
    ],
  },
  {
    id: "02",
    question: "What makes buying insurance feel confusing?",
    data: [
      { name: "Too many plan options", value: 36, color: "#2877FF" },
      { name: "Hard-to-understand terms", value: 33, color: "#70CAE3" },
      { name: "Hidden costs", value: 19, color: "#579AFF" },
      { name: "Long forms", value: 12, color: "#AED8FF" },
    ],
  },
  {
    id: "03",
    question: "What information do you check first before choosing a plan?",
    data: [
      { name: "Coverage amount", value: 45, color: "#2877FF" },
      { name: "Network hospitals", value: 23, color: "#70CAE3" },
      { name: "Premium pricing", value: 20, color: "#579AFF" },
      { name: "Waiting periods", value: 12, color: "#AED8FF" },
    ],
  },
  {
    id: "04",
    question: "What feature would help you choose faster?",
    data: [
      { name: "Plan comparison", value: 39, color: "#2877FF" },
      { name: "Guided questionnaire", value: 31, color: "#70CAE3" },
      { name: "Smart recommendations", value: 20, color: "#70A5FF" },
      { name: "Chat/agent support", value: 10, color: "#AED8FF" },
    ],
  },
  {
    id: "05",
    question: "What's your biggest fear while buying insurance online?",
    data: [
      { name: "Missing hidden conditions", value: 47, color: "#2877FF" },
      { name: "Buying the wrong plan", value: 29, color: "#70CAE3" },
      { name: "Claim rejection", value: 18, color: "#70A5FF" },
      { name: "Payment/security issues", value: 6, color: "#AED8FF" },
    ],
  },
  {
    id: "06",
    question: "What helps you trust an insurance platform more?",
    data: [
      { name: "Clear explanations", value: 34, color: "#2877FF" },
      { name: "Reviews & real stories", value: 27, color: "#70CAE3" },
      { name: "Transparent pricing", value: 26, color: "#70A5FF" },
      { name: "Brand reputation", value: 13, color: "#AED8FF" },
    ],
  },
];

const ChartReview = () => {
  // Plugin factory to create a plugin with the chart ID
  const createCenterLabelPlugin = (chartId: string) => {
    return {
      id: `centerLabel-${chartId}`,
      beforeDraw: (chart: ChartJS) => {
        const ctx = chart.ctx;
        const centerX = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
        const centerY = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;

        ctx.save();
        ctx.font = "bold 24px sans-serif";
        ctx.fillStyle = "#000929";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(chartId, centerX, centerY);
        ctx.restore();
      },
    };
  };

  const getChartData = (section: ChartSection) => {
    return {
      labels: section.data.map((item) => item.name),
      datasets: [
        {
          data: section.data.map((item) => item.value),
          backgroundColor: section.data.map((item) => item.color),
          borderWidth: 0,
          spacing: 2,
        },
      ],
    };
  };

  const getChartOptions = (): ChartOptions<"doughnut"> => {
    return {
      responsive: true,
      maintainAspectRatio: true,
      cutout: "60%",
      rotation: 50,
      circumference: 360,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    };
  };

  return (
    <ScreenLayout outerClassName="bg-white md:py-12 py-5">
      <div className="flex flex-col items-center justify-center">
        {/* Charts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 lg:gap-12">
          {chartSections.map((section) => {
            const chartData = getChartData(section);
            const chartOptions = getChartOptions();
            const centerLabelPlugin = createCenterLabelPlugin(section.id);

            return (
              <div key={section.id} className="bg-white w-full">
                {/* Donut Chart */}
                <div className="flex justify-start mb-6">
                  <div className="w-full max-w-[200px] h-[200px] relative">
                    <Doughnut
                      data={chartData}
                      options={chartOptions}
                      plugins={[centerLabelPlugin]}
                    />
                  </div>
                </div>
                <div>
                  {/* Question */}
                  <h3 className="text-xl md:text-2xl font-semibold text-[#000429CC] mb-6 min-h-[48px]">
                    {section.question}
                  </h3>
                </div>
                {/* Answers List */}
                <div className="space-y-3">
                  {section.data.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 sm:gap-3 text-sm md:text-base"
                    >
                      <div
                        className="w-8 h-4 sm:w-10 sm:h-5 rounded-[53px] shrink-0"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-[#00042987] flex-1 font-normal text-base sm:text-lg md:text-xl truncate">
                        {item.name}
                      </span>
                      <span className="text-[#00042987] font-bold text-base sm:text-lg md:text-xl shrink-0">
                        {item.value}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ScreenLayout>
  );
};

export default ChartReview;
